//@ts-strict-ignore
import { DocumentNode, useLazyQuery, useQuery } from '@apollo/client';
import { useHistory, useParams } from 'react-router';
import { EntityType } from '../../@types/entityTypes';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';

export const useUrlParamId = () => {
  const params = useParams();
  return getUrlId({}, { idParam: 'id', params: params });
};

export const getUrlId = (
  { formId, match, history }: { formId?: string; match?: any; history?: any },
  {
    idParam,
    idParamValueFn,
    fallback = true,
    params,
  }: { idParam: string; idParamValueFn?: (_: any) => string; fallback?: boolean; params?: { [key: string]: string } }
) => {
  const id = params?.[idParam];

  if ((!fallback && idParamValueFn) || (!id && idParamValueFn)) {
    return idParamValueFn({ formId, match, history });
  }

  return id || null;
};

type FormConfig = {
  onCompleted?: (data: any) => void;
  skip?: boolean;
  duplicate?: boolean;
  idParam?: string;
  idParamValueFn?: (_: any) => string;
  forceDefault?: boolean;
  defaultVariables?: object;
  additionalVariables?: object;
  overrideParams?: object;

  /**
   * Indicates this form is a single instance (Like a config) so no URL param is expected.
   */
  singleInstance?: boolean;
};

/**
 * Attempt at writing a hook to reimplement formQuery but merge the 'default' and 'formQuery' into one, since
 * both typically return data to the same prop and return depending on whether the form is new.
 */
export const useFormQuery = <FormData = any>(
  { instance, default: defaultQuery }: Pick<EntityType, 'instance' | 'default'>, //Queries
  {
    onCompleted,
    skip,
    duplicate = false,
    idParam = 'id',
    idParamValueFn,
    forceDefault = false,
    defaultVariables = {},
    additionalVariables: passedAdditionalVariables = {},
    overrideParams,
    singleInstance = true,
  }: FormConfig = {} //Parameters
) => {
  const locationParams = useParams(); //Be wary - Gets location params, may cause issues with modal capabilities.
  const history = useHistory<{ duplicateId?: string }>();
  const handleQueryError = useComponentQueryErrorHandling();
  const urlId = getUrlId({}, { idParam, idParamValueFn, params: overrideParams || locationParams }); //No first argument because provided idParamValueFn can access props directly if it's using hooks.
  const isDefault = !!(defaultQuery && (forceDefault || urlId === 'New'));
  const idVariable = isDefault && duplicate ? history.location?.state?.duplicateId : urlId;
  const additionalVariables = isDefault ? defaultVariables : passedAdditionalVariables;

  // Separate query to always fetch the *Instance* - Used in concurrency reload when transitioning from a new to persisted record (/New to /id).
  const [fetchInstance] = useLazyQuery(instance!, {
    variables: {
      id: idVariable,
      ...additionalVariables,
    },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: handleQueryError,
  });

  const { data, ...formData } = useQuery((isDefault ? defaultQuery : instance) as DocumentNode, {
    variables: {
      [idParam]: idVariable,
      ...additionalVariables,
    },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: skip || (!duplicate && !idVariable && !singleInstance) || (!isDefault && idVariable === 'New'),
    onCompleted: onCompleted,
    onError: handleQueryError,
  });

  const result: { defaultRecord?: FormData; data?: FormData; isDefault: boolean; [key: string]: any } = {
    ...formData,
    isDefault,
    fetchInstance: fetchInstance,
  };

  //Coerce existing format (Otherwise it's nested as 'data.data' when expecting just 'data').
  if (isDefault) {
    result.defaultRecord = data?.defaultRecord;
  } else {
    result.data = data?.data;
  }
  return result;
};
